import React from 'react';
import { useNavigate } from 'react-router-dom';
import { Award, ArrowRight, Plus, Check, X, Trash2 } from 'lucide-react';
import { useStaff } from '../hooks/useStaff';
import CityMultiSelect from '../components/CityMultiSelect';
import { parseStaffCities, joinStaffCities, formatStaffCities } from '../data/colombiaCities';

export default function StaffPage() {
  const {
    activeBusiness,
    staff,
    newStaff,
    setNewStaff,
    handleAddStaff,
    handleDeleteStaff,
    toggleStaffStatus,
    handleEditStaff,
    setStaffCity
  } = useStaff();
  const navigate = useNavigate();

  // Reparto por ciudad: solo tiene sentido en negocios de pedidos o con ciudades de entrega definidas.
  const cities = Array.isArray(activeBusiness?.delivery_cities) ? activeBusiness.delivery_cities : [];
  const showCity = activeBusiness?.flujo_operativo === 'pedidos' || cities.length > 0;
  const staffList = Array.isArray(staff) ? staff : (Array.isArray(activeBusiness?.staff) ? activeBusiness.staff : []);

  if (!activeBusiness) {
    return (
      <div className="p-8 text-center text-seryi-text">
        <div className="w-8 h-8 border-2 border-seryi-green border-t-transparent rounded-full animate-spin mx-auto mb-3" />
        <p className="text-xs">Cargando equipo de trabajo...</p>
      </div>
    );
  }

  return (
    <div className="space-y-6 max-w-4xl">
      <div>
        <h2 className="text-xl font-extrabold text-seryi-heading">Equipo de Trabajo</h2>
        <p className="text-xs text-seryi-text">Controla quiénes están disponibles en la base de datos para recibir citas de la IA de WhatsApp.</p>
      </div>

      <div className="bg-gradient-to-r from-seryi-bg to-seryi-bg-dark border border-seryi-bg-light p-4 rounded-2xl flex items-center justify-between gap-4">
        <div className="flex items-center gap-3">
          <div className="w-10 h-10 rounded-xl bg-seryi-green/10 flex items-center justify-center text-seryi-green shrink-0">
            <Award size={20} />
          </div>
          <div>
            <p className="text-xs font-bold text-seryi-heading">¿Deseas liquidar comisiones y exportar nómina?</p>
            <p className="text-[11px] text-seryi-text">Abre el nuevo módulo para calcular ganancias, márgenes y enviar desgloses por WhatsApp.</p>
          </div>
        </div>
        <button
          onClick={() => navigate('/v2/comisiones')}
          className="px-4 py-2 bg-seryi-green text-seryi-bg-dark hover:brightness-110 rounded-xl text-xs font-bold transition-all shrink-0 flex items-center gap-1.5"
        >
          <span>Ir a Nómina</span>
          <ArrowRight size={13} />
        </button>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        {/* Creador */}
        <div className="bg-seryi-bg border border-seryi-bg-light p-6 rounded-3xl space-y-4 self-start">
          <h3 className="font-bold text-xs uppercase text-seryi-heading tracking-wider">Nuevo Miembro</h3>
          
          <div className="space-y-1">
            <label className="text-[10px] uppercase text-seryi-text">Nombre Profesional</label>
            <input 
              type="text" 
              className="w-full bg-seryi-bg-dark border border-seryi-bg-light rounded-xl px-3 py-2 text-xs text-seryi-heading focus:outline-none"
              placeholder="Mateo"
              value={newStaff.name}
              onChange={(e) => setNewStaff(prev => ({ ...prev, name: e.target.value }))}
            />
          </div>

          <div className="space-y-1">
            <label className="text-[10px] uppercase text-seryi-text">Especialidad</label>
            <input 
              type="text" 
              className="w-full bg-seryi-bg-dark border border-seryi-bg-light rounded-xl px-3 py-2 text-xs text-seryi-heading focus:outline-none"
              placeholder="Diseños y Barba"
              value={newStaff.specialty}
              onChange={(e) => setNewStaff(prev => ({ ...prev, specialty: e.target.value }))}
            />
          </div>

          <div className="space-y-1">
            <label className="text-[10px] uppercase text-seryi-text">Correo (Gmail)</label>
            <input 
              type="email" 
              className="w-full bg-seryi-bg-dark border border-seryi-bg-light rounded-xl px-3 py-2 text-xs text-seryi-heading focus:outline-none"
              placeholder="empleado@gmail.com"
              value={newStaff.google_calendar_id}
              onChange={(e) => setNewStaff(prev => ({ ...prev, google_calendar_id: e.target.value }))}
            />
          </div>

          <div className="space-y-1">
            <label className="text-[10px] uppercase text-seryi-text">Teléfono (WhatsApp)</label>
            <input 
              type="text" 
              className="w-full bg-seryi-bg-dark border border-seryi-bg-light rounded-xl px-3 py-2 text-xs text-seryi-heading focus:outline-none"
              placeholder="57300..."
              value={newStaff.telefono}
              onChange={(e) => setNewStaff(prev => ({ ...prev, telefono: e.target.value }))}
            />
          </div>

          <div className="space-y-1">
            <label className="text-[10px] uppercase text-seryi-text">PIN de Acceso Portal Staff</label>
            <input 
              type="password" 
              maxLength={4}
              className="w-full bg-seryi-bg-dark border border-seryi-bg-light rounded-xl px-3 py-2 text-xs text-seryi-heading focus:outline-none tracking-widest text-center font-bold"
              placeholder="****"
              value={newStaff.pin_code}
              onChange={(e) => setNewStaff(prev => ({ ...prev, pin_code: e.target.value.replace(/\D/g, '') }))}
            />
          </div>

          {showCity && (
            <div className="space-y-1">
              <label htmlFor="staff-city" className="text-[10px] uppercase text-seryi-text">Ciudad de reparto</label>
              {cities.length > 0 ? (
                <CityMultiSelect
                  id="staff-city" label="Ciudad de reparto" options={cities}
                  value={parseStaffCities(newStaff.ciudad)}
                  onChange={(list) => setNewStaff(prev => ({ ...prev, ciudad: joinStaffCities(list) }))}
                />
              ) : (
                <input
                  id="staff-city" type="text"
                  className="w-full bg-seryi-bg-dark border border-seryi-bg-light rounded-xl px-3 py-2 text-xs text-seryi-heading focus:outline-none"
                  placeholder="Pereira, Dosquebradas (vacío = todas)"
                  value={newStaff.ciudad ? formatStaffCities(newStaff.ciudad) || newStaff.ciudad : ''}
                  onChange={(e) => setNewStaff(prev => ({ ...prev, ciudad: e.target.value.replace(/,/g, '|') }))}
                />
              )}
            </div>
          )}

          <div className="space-y-1">
            <label className="text-[10px] uppercase text-seryi-text">Comisión (%)</label>
            <input 
              type="number" 
              className="w-full bg-seryi-bg-dark border border-seryi-bg-light rounded-xl px-3 py-2 text-xs text-seryi-heading focus:outline-none"
              placeholder="50"
              value={newStaff.comision_porcentaje}
              onChange={(e) => setNewStaff(prev => ({ ...prev, comision_porcentaje: parseFloat(e.target.value) || 0 }))}
            />
          </div>

          <button 
            onClick={handleAddStaff}
            className="w-full bg-seryi-green text-seryi-bg-dark hover:bg-seryi-green/80 font-bold py-2.5 rounded-xl text-xs transition-all flex items-center justify-center gap-2"
          >
            <Plus size={14} /> Registrar Especialista
          </button>
        </div>

        {/* Listado */}
        <div className="lg:col-span-2 space-y-3">
          <h3 className="font-bold text-xs uppercase text-seryi-heading tracking-wider">Plantilla del Local ({staffList.length})</h3>
          {staffList.length === 0 ? (
            <div className="bg-seryi-bg border border-seryi-bg-light p-8 rounded-2xl text-center">
              <p className="text-xs text-seryi-text">No hay especialistas registrados en este negocio aún.</p>
              <p className="text-[10px] text-slate-500 mt-1">Usa el formulario a la izquierda para añadir al primer miembro de tu equipo.</p>
            </div>
          ) : (
            staffList.map(member => (
              <div key={member.id} className="bg-seryi-bg border border-seryi-bg-light p-4 rounded-2xl flex items-center justify-between gap-4">
                <div className="flex items-center gap-3">
                  <div className="w-10 h-10 bg-seryi-green/10 rounded-full flex items-center justify-center font-bold text-seryi-green border border-indigo-500/20">
                    {member.name ? member.name.charAt(0).toUpperCase() : '?'}
                  </div>
                  <div>
                    <p className="font-bold text-sm text-seryi-heading">{member.name || 'Sin Nombre'}</p>
                    <p className="text-xs text-seryi-text">{member.specialty || 'General'}</p>
                    <p className="text-[10px] text-seryi-text font-mono mt-1">{member.google_calendar_id || 'Calendario Google Conectado'}</p>
                    {showCity && (
                      <div className="mt-1.5 flex items-center gap-1.5">
                        <label htmlFor={`city-${member.id}`} className="text-[10px] text-seryi-text">📍 Ciudades:</label>
                        {cities.length > 0 ? (
                          <div className="min-w-[9rem] max-w-[16rem]">
                            <CityMultiSelect
                              id={`city-${member.id}`} label="Ciudades" compact emptyLabel="Todas" options={cities}
                              value={parseStaffCities(member.ciudad)}
                              onChange={(list) => setStaffCity(member.id, list)}
                            />
                          </div>
                        ) : (
                          <button
                            type="button" id={`city-${member.id}`}
                            onClick={() => { const v = window.prompt('Ciudades de reparto separadas por coma (vacío = todas):', formatStaffCities(member.ciudad)); if (v !== null) setStaffCity(member.id, v); }}
                            className="text-[11px] font-bold text-seryi-heading underline"
                          >
                            {formatStaffCities(member.ciudad) || 'Todas (cambiar)'}
                          </button>
                        )}
                      </div>
                    )}
                  </div>
                </div>

                <div className="flex items-center gap-2">
                  <button 
                    onClick={() => toggleStaffStatus(member.id)}
                    className={`px-3 py-1.5 rounded-lg text-xs font-bold flex items-center gap-1.5 transition-all ${member.available ? 'bg-emerald-500/10 text-emerald-400 border border-emerald-500/20' : 'bg-rose-500/10 text-rose-400 border border-rose-500/20'}`}
                  >
                    {member.available ? <Check size={12} /> : <X size={12} />}
                    {member.available ? "Disponible" : "Fuera"}
                  </button>
                  <button 
                    onClick={() => handleEditStaff(member)}
                    title="Editar Especialista"
                    className="p-2 text-seryi-text hover:text-seryi-green hover:bg-seryi-green/10 rounded-lg transition-all"
                  >
                    ✏️
                  </button>
                  <button 
                    onClick={() => handleDeleteStaff(member.id)}
                    title="Eliminar Especialista"
                    className="p-2 text-seryi-text hover:text-rose-400 hover:bg-rose-500/10 rounded-lg transition-all"
                  >
                    <Trash2 size={14} />
                  </button>
                </div>
              </div>
            ))
          )}
        </div>
      </div>
    </div>
  );
}
